/**
 * Builds every [data-mapbox] element on the page from its JSON config (see class.Mapbox.php).
 * Mapbox sits behind functional cookies, so without consent the map waits for one of the accept buttons.
 */
window.dcMapbox = (() => {
    "use strict"

    const CDN = document.currentScript.dataset;
    const MARKER_HEIGHT = 40; // px tall at icon-size 1
    const OVERLAY_GAP = 200; // px between an overlay's edge and the map centre (see avoidOverlay)

    // Rasterise the SVG marker to a canvas so Mapbox can use it as an icon.
    // Drawn at the SVG's natural size (241x381, same as the Studio sprite icon) so both render identically
    async function addMarkerImage(map, src) {
        const img = new Image();
        img.src = src;
        await img.decode();

        const pixelRatio = 2;
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth * pixelRatio;
        canvas.height = img.naturalHeight * pixelRatio;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        map.addImage('dc-marker', ctx.getImageData(0, 0, canvas.width, canvas.height), { pixelRatio });

        return img.naturalHeight;
    }

    function addMarkerLayer(map, markers, iconHeight) {
        const scale = MARKER_HEIGHT / iconHeight;

        map.addSource('markers', { type: 'geojson', data: markers });
        map.addLayer({
            id: 'marker-layer',
            type: 'symbol',
            source: 'markers',
            layout: {
                'icon-image': [
                    'match',
                    ['get', 'type'],
                    'our-location', ['coalesce', ['image', 'DevonConversionsIcon'], ['image', 'dc-marker']], // Studio sprite icon, falling back to the inline SVG
                    'diamond' // Default fallback icon
                ],
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'icon-size': [
                    'interpolate', ['linear'], ['zoom'],
                    9, 0.5 * scale,  // 20px tall
                    15, 2.5 * scale  // 100px tall
                ]
            }
        });
    }

    function addHoverPopup(map) {
        const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
        const target = { layerId: 'marker-layer' };

        map.addInteraction('marker-mouseenter', {
            type: 'mouseenter',
            target,
            handler: ({ feature }) => {
                map.getCanvas().style.cursor = 'pointer';
                if (feature.properties.description) {
                    popup.setLngLat(feature.geometry.coordinates).setHTML(feature.properties.description).addTo(map);
                }
            }
        });

        map.addInteraction('marker-mouseleave', {
            type: 'mouseleave',
            target,
            handler: () => {
                map.getCanvas().style.cursor = '';
                popup.remove();
            }
        });
    }

    // Clicking a marker scrolls to the page element sharing its id
    function addScrollOnClick(map) {
        map.addInteraction('marker-click', {
            type: 'click',
            target: { layerId: 'marker-layer' },
            handler: ({ feature }) => {
                document.getElementById(feature.properties.id)?.scrollIntoView({ behavior: 'smooth' });
            }
        });
    }

    // Starts the map centre just right of an overlay that covers the map, rather than hidden behind it.
    // Re-measured on resize, so when the overlay stacks below the map (mobile) the centre is reset to the middle
    function avoidOverlay(map, container, selector) {
        const overlay = document.querySelector(selector);
        if (!overlay) return;

        const update = () => {
            const m = container.getBoundingClientRect();
            const o = overlay.getBoundingClientRect();
            const overlaps = o.left < m.right && o.right > m.left && o.top < m.bottom && o.bottom > m.top;

            // Uneven left/right padding moves the centre to x (the centre sits midway between the paddings)
            const x = overlaps ? Math.min(o.right - m.left + OVERLAY_GAP, m.width - OVERLAY_GAP) : m.width / 2;
            const shift = 2 * x - m.width;

            map.setPadding({ top: 0, bottom: 0, left: Math.max(0, shift), right: Math.max(0, -shift) });
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(container);
        observer.observe(overlay);
    }

    function init(container) {
        const config = JSON.parse(container.dataset.mapbox);
        container.innerHTML = '';

        mapboxgl.accessToken = config.accessToken;
        const map = new mapboxgl.Map({ container, ...config.options });
        if (config.avoid) avoidOverlay(map, container, config.avoid);

        map.on('load', async () => {
            const iconHeight = await addMarkerImage(map, config.markerIcon);
            addMarkerLayer(map, config.markers, iconHeight);
            addHoverPopup(map);
            if (config.scrollOnClick) addScrollOnClick(map);
        });
    }

    function initAll() {
        document.querySelectorAll('[data-mapbox]').forEach((el) => init(el));
    }

    // Pull in Mapbox from the CDN, then build the maps
    let loading = false;
    function load() {
        if (loading) return;
        loading = true;

        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = CDN.stylesheet;
        document.head.appendChild(css);

        const script = document.createElement('script');
        script.src = CDN.script;
        script.onload = initAll;
        document.head.appendChild(script);
    }

    // "Accept & Load" button on the map placeholder
    function enable() {
        fetch('/scripts/enable-functional-cookie.php', { method: 'POST' });
        document.getElementById('biscuit-notice')?.classList.add('hidden');

        // Loads this map and anything else waiting on functional cookies (e.g. video embeds)
        document.dispatchEvent(new CustomEvent('cookieconsent', { detail: { functional: true } }));
    }

    // With consent, Mapbox::loadCdn() has already put Mapbox on the page
    window.addEventListener('load', () => {
        if (window.mapboxgl) initAll();
    });

    // Accepting functional cookies from the cookie banner
    document.addEventListener('cookieconsent', (e) => {
        if (e.detail.functional) load();
    });

    return { enable };
})();
